/**
 * What the administrator is shown before anything is sent (spec §11.2).
 *
 * Four numbers — imported, valid, invalid, duplicate — and a reason on every
 * row that is not going out. The numbers have to add up: `imported` is every
 * row that was read, and the other three account for all of it, because a
 * count that does not reconcile is exactly the thing that makes someone wonder
 * what happened to the missing eleven.
 *
 * Pure by construction (PLAN §4).
 */

import {
  buildRecipients,
  duplicateGroups,
  type BuildRecipientsOptions,
  type DuplicateGroup,
  type ParsedPair,
  type Recipient,
  type RecipientProblem,
} from './recipients.ts';

/**
 * `MAX_IMPORT_ROWS` (PLAN §13). A default rather than a lookup: this file reads
 * no environment, so the server passes its own value in.
 */
export const DEFAULT_MAX_ROWS = 10_000;

export interface ValidationTotals {
  /** Rows read, whatever became of them. */
  readonly imported: number;
  readonly valid: number;
  readonly invalid: number;
  readonly duplicate: number;
}

export interface ValidationOutcome {
  /** Every row, in the order it arrived, including the ones that cannot be sent. */
  readonly recipients: readonly Recipient[];
  readonly totals: ValidationTotals;
  /** True when the list is longer than the limit allows. Nothing is dropped — it is refused. */
  readonly overLimit: boolean;
  readonly limit: number;
  /** Numbers claimed by more than one row, and which row won (specification §16). */
  readonly duplicates: readonly DuplicateGroup[];
}

export interface ValidateOptions extends BuildRecipientsOptions {
  readonly maxRows?: number;
}

export function validatePairs(
  pairs: readonly ParsedPair[],
  options: ValidateOptions = {},
): ValidationOutcome {
  const limit = options.maxRows ?? DEFAULT_MAX_ROWS;
  const recipients = buildRecipients(pairs, options);

  return {
    recipients,
    totals: totalsOf(recipients),
    duplicates: duplicateGroups(recipients),
    // Over the limit the whole import is refused rather than trimmed to fit.
    // Trimming would send a campaign that looks complete and is not — the same
    // failure as truncating a two-block paste to the shorter list.
    overLimit: recipients.length > limit,
    limit,
  };
}

export function totalsOf(recipients: readonly Recipient[]): ValidationTotals {
  let valid = 0;
  let invalid = 0;
  let duplicate = 0;

  for (const recipient of recipients) {
    if (recipient.status === 'valid') valid += 1;
    else if (recipient.status === 'duplicate') duplicate += 1;
    else invalid += 1;
  }

  return { imported: recipients.length, valid, invalid, duplicate };
}

/** Whether this list could be sent as it stands. */
export function isSendable(outcome: ValidationOutcome): boolean {
  return !outcome.overLimit && outcome.totals.valid > 0;
}

/**
 * The message key for each reason a row cannot be sent.
 *
 * Keys rather than sentences, and plain strings rather than the server's
 * `MessageKey` type: `domain/` must not import the i18n module, which reads
 * files. A test asserts that every key here exists in both catalogues, which is
 * the guarantee the type would have given.
 */
export const PROBLEM_MESSAGE_KEYS: Readonly<Record<RecipientProblem, string>> = {
  empty: 'row.phoneMissing',
  'invalid-characters': 'row.phoneInvalidCharacters',
  'unsupported-country': 'row.phoneUnsupportedCountry',
  'wrong-length': 'row.phoneWrongLength',
  'unknown-format': 'row.phoneUnknownFormat',
  'missing-separator': 'row.missingSeparator',
  'ambiguous-separator': 'row.ambiguousSeparator',
  'empty-code': 'row.codeMissing',
  duplicate: 'row.duplicate',
};

export function problemMessageKey(problem: RecipientProblem): string {
  return PROBLEM_MESSAGE_KEYS[problem];
}
